import { create } from "zustand";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../lib/axiosInstance";

const useConsultantSubscriptionStore = create((set, get) => ({
    offer: null,
    history: [],
    isLoading: false,
    activeConsultants: [],

    // Fetch active subscription offer from admin
    fetchOffer: async () => {
        set({ isLoading: true });
        try {
            const response = await axiosInstancev1.get("/consultants/subscription/offer");

            if (response.data.success) {
                set({ offer: response.data.offer });
                return response.data.offer;
            } else {
                toast.error(response.data.message || "No active subscription offer found");
                set({ offer: null });
                return null;
            }
        } catch (error) {
            console.error("Error fetching subscription offer:", error);
            toast.error(error.response?.data?.message || "Failed to fetch subscription offer");
            set({ offer: null });
            return null;
        } finally {
            set({ isLoading: false });
        }
    },

    // Create Cashfree order for subscription payment
    createOrder: async (consultantProfileId, amount, returnUrl) => {
        set({ isLoading: true });
        try {
            const response = await axiosInstancev1.post("/consultants/subscription/create-order", {
                consultantProfileId,
                amount,
                returnUrl,
            });

            if (response.data.success) {
                return response.data.orderDetails;
            } else {
                toast.error(response.data.message || "Failed to create order");
                return null;
            }
        } catch (error) {
            console.error("Error creating subscription order:", error);
            toast.error(error.response?.data?.message || "Failed to create subscription order");
            return null;
        } finally {
            set({ isLoading: false });
        }
    },

    // Verify payment after Cashfree redirect
    verifyPayment: async (orderId) => {
        set({ isLoading: true });
        try {
            const response = await axiosInstancev1.post("/consultants/subscription/verify", {
                orderId,
            });

            if (response.data.success) {
                toast.success("Payment verified successfully! Your subscription is now active.");
                // Refresh subscription history
                get().fetchHistory();
                return response.data.subscription;
            } else {
                toast.error(response.data.message || "Payment verification failed");
                return null;
            }
        } catch (error) {
            console.error("Error verifying payment:", error);
            toast.error(error.response?.data?.message || "Payment verification failed");
            return null;
        } finally {
            set({ isLoading: false });
        }
    },

    // Fetch subscription history
    fetchHistory: async () => {
        set({ isLoading: true });
        try {
            const response = await axiosInstancev1.get("/consultants/subscription/history");

            if (response.data.success) {
                set({ history: response.data.history });
                return response.data.history;
            } else {
                set({ history: [] });
                return [];
            }
        } catch (error) {
            console.error("Error fetching subscription history:", error);
            toast.error(error.response?.data?.message || "Failed to fetch subscription history");
            set({ history: [] });
            return [];
        } finally {
            set({ isLoading: false });
        }
    },

    // Fetch active paid consultants (public API for discovery)
    fetchActiveConsultants: async (district = null, taluk = null) => {
        set({ isLoading: true });
        try {
            const params = {};
            if (district) params.district = district;
            if (taluk) params.taluk = taluk;

            const response = await axiosInstancev1.get("/consultants/subscription/active-consultants", { params });

            if (response.data.success) {
                set({ activeConsultants: response.data.consultants });
                return response.data.consultants;
            } else {
                set({ activeConsultants: [] });
                return [];
            }
        } catch (error) {
            console.error("Error fetching active consultants:", error);
            toast.error(error.response?.data?.message || "Failed to fetch consultants");
            set({ activeConsultants: [] });
            return [];
        } finally {
            set({ isLoading: false });
        }
    },
}));

export default useConsultantSubscriptionStore;
